import { FieldPolicy } from '@apollo/client';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';

import {
  GetEventLogsQueryGQLType,
  EventLogPaginatedFeedParamsGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';

// Helpers
export const generateEventLogCacheKey = (
  params: EventLogPaginatedFeedParamsGQLType,
) => {
  return [
    `eventLogTypes:${[...params.eventLogTypes].sort().join(',')}`,
    params.entityIds && `entityIds:${[...params.entityIds].sort().join(',')}`,
    params.startDate && `startDate:${params.startDate}`,
    params.endDate && `endDate:${params.endDate}`,
    params.limit && `limit:${params.limit}`,
    params.actorIds && `actorIds:${[...params.actorIds].sort().join(',')}`,
    params.creditFacilityIds &&
      `cfIds:${params.creditFacilityIds.sort().join(',')}`,
  ]
    .filter(Boolean)
    .join('|');
};

// Exports
export const eventLogsFieldPolicy: FieldPolicy = {
  // `keyArgs` is how you generate cache keys for a given GQL object. `eventLogs` is a
  // cursor-paginated query, which requires us to manually merge in paginated items.
  // Default caching behavior would invalidate the whole query whenever pagination params
  // change, which is not quite what we want.
  //
  // The desired caching behavior is as follows:
  //
  // 1. Cache query separately based on the `entityIds` and `eventLogTypes`. This keeps
  // results for different drawers separate, so we don't get items from one feed showing
  // up in another. This handles the case where users navigate between different routes
  // 2. Within the cached results for a given `entityIds+eventLogTypes` query, as paginated
  // items come in, we manually merge them into existing cached items. We also have to
  // update the `hasMore` field, which changes based on the latest set of received paginated
  // items
  keyArgs: (args) => {
    try {
      const params: EventLogPaginatedFeedParamsGQLType = args?.params;
      return generateEventLogCacheKey(params);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  },
  merge: (
    existing: GetEventLogsQueryGQLType['eventLogs'] | undefined,
    incoming: GetEventLogsQueryGQLType['eventLogs'],
    config,
  ) => {
    return {
      hasMore: incoming.hasMore,
      items:
        // This is just a fancy way to append incoming items to the end of the array
        // that's already in the cache.
        _.uniqBy([...(existing?.items ?? []), ...incoming.items], (item) =>
          // Items in the cache can sometimes resolve to a "ref", basically a pointer
          // to an item that was cached as a result of a different query. Unlikely to
          // come up with event log pagination, but, just in case, using the `readField`
          // helper to follow the ref/pointer to wherever the item is actually cached.
          config.readField<string>('eventId', item),
        ),
    };
  },
};
